import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, Clock, Users, Phone, Mail, MessageSquare, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { CreateReservationData, AvailabilityResponse } from '../../types';
import apiService from '../../services/api';
import { sanitizeText, sanitizeEmail, sanitizePhone, detectXSSPatterns } from '../../utils/security';

const ReservationForm: React.FC = () => {
  const navigate = useNavigate();
  const { restaurant } = useRestaurant();
  const { isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    nome_cliente: '',
    telefone_cliente: '',
    email_cliente: '',
    data_reserva: '',
    hora_reserva: '',
    numero_pessoas: '',
    observacoes: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [completedReservation, setCompletedReservation] = useState<any>(null);

  // Utility functions
  const getMinDate = () => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  };

  const getMaxDate = () => {
    const now = new Date();
    const maxDays = restaurant?.config_reservas?.antecedencia_maxima_dias || 30;
    const maxDate = new Date(now.getTime() + maxDays * 24 * 60 * 60 * 1000);
    return maxDate.toISOString().slice(0, 10);
  };

  // Check if a given date is a closed day
  const isClosedDay = (dateString: string) => {
    if (!restaurant) return false;

    const date = new Date(dateString);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const dayNames = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
    const dayName = dayNames[dayOfWeek];

    // Check if restaurant has horarios (schedule) configuration
    if (restaurant.horarios && restaurant.horarios[dayName]) {
      // Day is closed if ativo is false
      if (!restaurant.horarios[dayName].ativo) {
        return true;
      }
    }

    return false;
  };

  // Format phone input
  const formatPhoneInput = (value: string) => {
    let formatted = value.replace(/\D/g, '');
    
    if (formatted.length > 0 && !formatted.startsWith('351')) {
      if (formatted.length === 9) {
        formatted = '351' + formatted;
      }
    }
    
    if (formatted.startsWith('351')) {
      formatted = formatted.replace(/^351/, '+351 ');
      if (formatted.length > 8) {
        formatted = formatted.slice(0, 8) + ' ' + formatted.slice(8);
      }
    }
    
    return formatted;
  };

  // Generate time slots based on availability data
  const generateTimeSlots = () => {
    const allSlots = [];
    
    for (let hour = 12; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        allSlots.push({ value: time, label: time, disabled: false });
      }
    }
    
    // If we have availability data, update availability
    if (availability && availability.horarios) {
      return allSlots.map(slot => {
        const availableSlot = availability.horarios.find(h => h.hora === slot.value);
        return {
          ...slot,
          disabled: !availableSlot?.disponivel,
          available_capacity: availableSlot?.capacidade_total || 0,
          label: availableSlot?.disponivel ? slot.value : `${slot.value} (Esgotado)`
        };
      });
    }
    
    return allSlots;
  };

  // Generate number of people options
  const generatePeopleOptions = () => {
    const maxCapacity = 10; // Default max capacity
    return Array.from({length: maxCapacity}, (_, i) => i + 1);
  };

  // Handle form field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear specific field error
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Check availability when date is selected
  useEffect(() => {
    if (formData.data_reserva && restaurant?.slug) {
      const checkAvailability = async () => {
        try {
          setAvailabilityLoading(true);
          setAvailability(null);
          
          // Clear time and people selection when checking new date
          if (formData.hora_reserva) {
            handleFieldChange('hora_reserva', '');
          }
          if (formData.numero_pessoas) {
            handleFieldChange('numero_pessoas', '');
          }
          
          const availabilityData = await apiService.checkAvailability(restaurant.slug, formData.data_reserva);
          setAvailability(availabilityData);
          
        } catch (error) {
          console.error('Error checking availability:', error);
          setAvailability(null);
        } finally {
          setAvailabilityLoading(false);
        }
      };

      checkAvailability();
    } else {
      setAvailability(null);
      setAvailabilityLoading(false);
    }
  }, [formData.data_reserva, restaurant?.slug]);

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome_cliente.trim()) {
      newErrors.nome_cliente = 'Nome é obrigatório';
    }

    if (!formData.telefone_cliente.trim()) {
      newErrors.telefone_cliente = 'Telefone é obrigatório';
    }

    if (!formData.email_cliente.trim()) {
      newErrors.email_cliente = 'Email é obrigatório';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email_cliente)) {
        newErrors.email_cliente = 'Formato de email inválido';
      }
    }

    if (!formData.data_reserva) {
      newErrors.data_reserva = 'Data é obrigatória';
    } else if (isClosedDay(formData.data_reserva)) {
      newErrors.data_reserva = 'Restaurante fechado neste dia da semana';
    }

    if (!formData.hora_reserva) {
      newErrors.hora_reserva = 'Hora é obrigatória';
    }

    if (!formData.numero_pessoas) {
      newErrors.numero_pessoas = 'Número de pessoas é obrigatório';
    }

    // Check if date/time is in the future
    if (formData.data_reserva && formData.hora_reserva) {
      const selectedDateTime = new Date(`${formData.data_reserva}T${formData.hora_reserva}`);
      
      if (selectedDateTime <= new Date()) {
        newErrors.data_reserva = 'Data e hora devem ser futuras';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!restaurant) {
      setError('Informações do restaurante não disponíveis');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Sanitize all user inputs to prevent XSS
      const sanitizedNome = sanitizeText(formData.nome_cliente.trim());
      const sanitizedEmail = sanitizeEmail(formData.email_cliente);
      const sanitizedPhone = sanitizePhone(formData.telefone_cliente);
      const sanitizedObservacoes = sanitizeText(formData.observacoes?.trim() || '');

      // Check for XSS patterns in inputs
      if (detectXSSPatterns(formData.nome_cliente) || 
          detectXSSPatterns(formData.email_cliente) ||
          detectXSSPatterns(formData.observacoes || '')) {
        setError('Entrada inválida detectada. Por favor, não use caracteres especiais HTML.');
        setIsLoading(false);
        return;
      }

      // Validate sanitized data
      if (!sanitizedNome || !sanitizedEmail || !sanitizedPhone) {
        setError('Erro ao processar os dados do formulário. Verifique os campos.');
        setIsLoading(false);
        return;
      }

      const selectedDateTime = new Date(`${formData.data_reserva}T${formData.hora_reserva}`);

      const reservationData: CreateReservationData = {
        restaurante_id: restaurant.id,
        nome_cliente: sanitizedNome,
        telefone_cliente: sanitizedPhone,
        email_cliente: sanitizedEmail,
        data_hora: selectedDateTime.toISOString(),
        numero_pessoas: parseInt(formData.numero_pessoas),
        observacoes: sanitizedObservacoes || undefined,
      };

      // Use manager endpoint if authenticated (auto-confirmed, no payment required)
      // Use public endpoint if not authenticated (may require payment, pending status)
      const reservation = isAuthenticated 
        ? await apiService.createReservationAsManager(reservationData)
        : await apiService.createReservation(reservationData);
      
      // Store completed reservation for summary display
      setCompletedReservation({
        ...reservation,
        nome_cliente: formData.nome_cliente.trim(),
        telefone_cliente: formData.telefone_cliente,
        email_cliente: formData.email_cliente.trim(),
        data_reserva: formData.data_reserva,
        hora_reserva: formData.hora_reserva,
        numero_pessoas: formData.numero_pessoas,
        observacoes: formData.observacoes?.trim() || '',
        restaurante_nome: restaurant?.nome || 'Restaurante'
      });
      
      setSuccess(true);
      
      // Reset form
      setFormData({
        nome_cliente: '',
        telefone_cliente: '',
        email_cliente: '',
        data_reserva: '',
        hora_reserva: '',
        numero_pessoas: '',
        observacoes: ''
      });
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar reserva. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Legacy handleChange for compatibility
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'telefone_cliente') {
      const formatted = formatPhoneInput(value);
      handleFieldChange(name, formatted);
    } else {
      handleFieldChange(name, type === 'number' ? value : value);
    }
  };

  // Reservation Summary Component
  const ReservationSummary = ({ reservation, onNewReservation }: any) => {
    // Determine if reservation is auto-confirmed (manager) or pending (public)
    const isConfirmed = reservation.status === 'confirmada';
    const isManagerCreated = reservation.payment_status === 'not_required' && isConfirmed;
    
    return (
    <div className="max-w-2xl mx-auto">
      <div className="card p-6">
        <div className="text-center mb-6">
          <div className={`mx-auto h-16 w-16 ${isConfirmed ? 'bg-green-100' : 'bg-yellow-100'} rounded-full flex items-center justify-center mb-4`}>
            <CheckCircle className={`h-8 w-8 ${isConfirmed ? 'text-green-600' : 'text-yellow-600'}`} />
          </div>
          <h2 className="text-2xl font-bold text-secondary-900 mb-2">
            {isConfirmed ? '✅ Reserva Confirmada!' : '⏳ Reserva Criada!'}
          </h2>
          <p className="text-secondary-600 mb-2">
            Reserva #{reservation.id || 'N/A'}
          </p>
          {isManagerCreated && (
            <div className="inline-block bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full font-medium">
              Confirmada Automaticamente • Sem Pagamento Necessário
            </div>
          )}
          {!isConfirmed && (
            <div className="inline-block bg-yellow-100 text-yellow-800 text-xs px-3 py-1 rounded-full font-medium">
              Aguarda Confirmação
            </div>
          )}
        </div>

        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <h3 className="text-lg font-medium text-green-800 mb-3">📋 Detalhes da Reserva</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-green-600 font-medium">Restaurante:</span>
              <p className="text-green-800 font-bold">{reservation.restaurante_nome}</p>
            </div>
            
            <div>
              <span className="text-sm text-green-600 font-medium">Nome:</span>
              <p className="text-green-800 font-bold">{reservation.nome_cliente}</p>
            </div>
            
            <div>
              <span className="text-sm text-green-600 font-medium">Data:</span>
              <p className="text-green-800 font-bold">
                {new Date(reservation.data_reserva).toLocaleDateString('pt-PT')}
              </p>
            </div>
            
            <div>
              <span className="text-sm text-green-600 font-medium">Hora:</span>
              <p className="text-green-800 font-bold">{reservation.hora_reserva}</p>
            </div>
            
            <div>
              <span className="text-sm text-green-600 font-medium">Pessoas:</span>
              <p className="text-green-800 font-bold">
                {reservation.numero_pessoas} {reservation.numero_pessoas === '1' ? 'pessoa' : 'pessoas'}
              </p>
            </div>
            
            <div>
              <span className="text-sm text-green-600 font-medium">Telefone:</span>
              <p className="text-green-800 font-bold">{reservation.telefone_cliente}</p>
            </div>
            
            <div className="sm:col-span-2">
              <span className="text-sm text-green-600 font-medium">Email:</span>
              <p className="text-green-800 font-bold">{reservation.email_cliente}</p>
            </div>
            
            {reservation.observacoes && (
              <div className="sm:col-span-2">
                <span className="text-sm text-green-600 font-medium">Observações:</span>
                <p className="text-green-800 italic">"{reservation.observacoes}"</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>💡 Importante:</strong> Chegue ao restaurante com 15 minutos de antecedência. 
            Em caso de atraso superior a 15 minutos, a mesa poderá ser libertada.
          </p>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={onNewReservation}
            className="btn-secondary flex-1"
          >
            Fazer Nova Reserva
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/reservations')}
            className="btn-primary flex-1"
          >
            Ver Todas as Reservas
          </button>
        </div>
      </div>
    </div>
    );
  };

  // Show reservation summary if reservation was completed
  if (completedReservation) {
    return (
      <ReservationSummary 
        reservation={completedReservation}
        onNewReservation={() => setCompletedReservation(null)}
      />
    );
  }

  const timeSlots = generateTimeSlots();
  const peopleOptions = generatePeopleOptions();

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">Reservar Mesa</h1>
        <p className="text-secondary-600">{restaurant?.nome || 'Restaurante'}</p>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Manager Info Banner */}
        {isAuthenticated && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800 mb-1">
                  Reserva Automática de Staff
                </h3>
                <p className="text-sm text-green-700">
                  Esta reserva será <strong>confirmada automaticamente</strong> sem necessidade de pagamento. 
                  Ideal para reservas por telefone ou presenciais.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Customer Information */}
        <div className="space-y-4">
          <div>
            <label htmlFor="nome_cliente" className="label">
              Nome Completo *
            </label>
            <input
              type="text"
              id="nome_cliente"
              name="nome_cliente"
              required
              className={`input-field ${errors.nome_cliente ? 'border-red-500' : ''}`}
              placeholder="Nome completo"
              value={formData.nome_cliente}
              onChange={handleChange}
            />
            {errors.nome_cliente && (
              <p className="text-sm text-red-600 mt-1">{errors.nome_cliente}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="telefone_cliente" className="label">
                Telefone *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
                <input
                  type="tel"
                  id="telefone_cliente"
                  name="telefone_cliente"
                  required
                  className={`input-field pl-10 ${errors.telefone_cliente ? 'border-red-500' : ''}`}
                  placeholder="+351 912 345 678"
                  value={formData.telefone_cliente}
                  onChange={handleChange}
                />
              </div>
              {errors.telefone_cliente && (
                <p className="text-sm text-red-600 mt-1">{errors.telefone_cliente}</p>
              )}
            </div>

            <div>
              <label htmlFor="email_cliente" className="label">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
                <input
                  type="email"
                  id="email_cliente"
                  name="email_cliente"
                  required
                  className={`input-field pl-10 ${errors.email_cliente ? 'border-red-500' : ''}`}
                  placeholder="seu@email.pt"
                  value={formData.email_cliente}
                  onChange={handleChange}
                />
              </div>
              {errors.email_cliente && (
                <p className="text-sm text-red-600 mt-1">{errors.email_cliente}</p>
              )}
            </div>
          </div>
        </div>

        {/* Reservation Details */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="data_reserva" className="label">
                Data da Reserva *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
                <input
                  type="date"
                  id="data_reserva"
                  name="data_reserva"
                  required
                  min={getMinDate()}
                  max={getMaxDate()}
                  className={`input-field pl-10 ${errors.data_reserva ? 'border-red-500' : ''}`}
                  value={formData.data_reserva}
                  onChange={handleChange}
                />
              </div>
              {errors.data_reserva && (
                <p className="text-sm text-red-600 mt-1">{errors.data_reserva}</p>
              )}
            </div>

            <div>
              <label htmlFor="hora_reserva" className="label">
                {availabilityLoading 
                  ? 'Verificando disponibilidade...' 
                  : !formData.data_reserva 
                    ? 'Selecione primeiro uma data' 
                    : 'Hora da Reserva *'
                }
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
                <select
                  id="hora_reserva"
                  name="hora_reserva"
                  required
                  disabled={!formData.data_reserva || availabilityLoading}
                  className={`input-field pl-10 ${errors.hora_reserva ? 'border-red-500' : ''} ${(!formData.data_reserva || availabilityLoading) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  value={formData.hora_reserva}
                  onChange={handleChange}
                >
                  <option value="">
                    {availabilityLoading 
                      ? 'Carregando horários...' 
                      : !formData.data_reserva 
                        ? 'Selecione uma data primeiro' 
                        : 'Selecione um horário'
                    }
                  </option>
                  {timeSlots.map((slot: any) => (
                    <option 
                      key={slot.value} 
                      value={slot.isSeparator ? '' : slot.value}
                      disabled={slot.disabled}
                      style={slot.isSeparator ? { backgroundColor: '#f5f5f5', fontWeight: 'bold', fontStyle: 'italic' } : {}}
                    >
                      {slot.label}
                    </option>
                  ))}
                </select>
                {availabilityLoading && (
                  <div className="absolute right-10 top-3">
                    <div className="spinner h-4 w-4" />
                  </div>
                )}
              </div>
              {availabilityLoading && (
                <p className="text-sm text-blue-600 mt-1">Carregando horários disponíveis...</p>
              )}
              {!availabilityLoading && !formData.data_reserva && (
                <p className="text-sm text-gray-600 mt-1">Selecione uma data para ver os horários disponíveis</p>
              )}
              {errors.hora_reserva && (
                <p className="text-sm text-red-600 mt-1">{errors.hora_reserva}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="numero_pessoas" className="label">
              {!formData.data_reserva 
                ? 'Selecione primeiro uma data'
                : !formData.hora_reserva
                  ? 'Selecione primeiro uma hora'
                  : availabilityLoading
                    ? 'Verificando disponibilidade...'
                    : 'Número de Pessoas *'
              }
            </label>
            <div className="relative">
              <Users className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
              <select
                id="numero_pessoas"
                name="numero_pessoas"
                required
                disabled={!formData.data_reserva || !formData.hora_reserva || availabilityLoading}
                className={`input-field pl-10 ${errors.numero_pessoas ? 'border-red-500' : ''} ${(!formData.data_reserva || !formData.hora_reserva || availabilityLoading) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                value={formData.numero_pessoas}
                onChange={handleChange}
              >
                <option value="">
                  {!formData.data_reserva 
                    ? 'Selecione uma data primeiro'
                    : !formData.hora_reserva
                      ? 'Selecione uma hora primeiro'
                      : availabilityLoading
                        ? 'Carregando...'
                        : 'Selecione o número de pessoas'
                  }
                </option>
                {peopleOptions.map(num => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? 'pessoa' : 'pessoas'}
                  </option>
                ))}
              </select>
            </div>
            {!formData.data_reserva && (
              <p className="text-sm text-gray-600 mt-1">Selecione uma data para continuar</p>
            )}
            {formData.data_reserva && !formData.hora_reserva && (
              <p className="text-sm text-gray-600 mt-1">Selecione uma hora para ver lugares disponíveis</p>
            )}
            {errors.numero_pessoas && (
              <p className="text-sm text-red-600 mt-1">{errors.numero_pessoas}</p>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-4">
          <div>
            <label htmlFor="observacoes" className="label">
              Observações (opcional)
            </label>
            <textarea
              id="observacoes"
              name="observacoes"
              rows={4}
              className="input-field"
              placeholder="Alguma observação especial para a sua reserva..."
              value={formData.observacoes}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/reservations')}
            className="btn-secondary flex-1"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary flex-1 flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="spinner" />
                <span>Criando reserva...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Fazer Reserva</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReservationForm;
